import { useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { QUESTION_STATUSES } from '@/helpers/constants';
import { getFlagURL, getNameInitials } from '@/helpers/utils';
import { useCreateQuestion } from '@/query/queries/question/useCreateQuestion';
import { remainingTime } from '@/helpers/date';
import { APP_LINKS } from '@/helpers/links';
import Notify from '@/components/Notification';
import { Avatar } from '@/components/UI/Avatar';
import { DropdownBase } from '@/components/Dropdown/base';
import { WriteMessage } from '@/components/WriteMessage';
import { RoundLink } from '@/components/UI/RoundLink';
import { useGetMe } from '@/hooks/useGetMe';
import s from './index.module.scss';

export const QuestionCard = ({ question }) => {
  const [replyMessageID, setReplyMessageID] = useState(null);
  const { isVerified } = useGetMe();
  const { mutateAsync: createNewQuestion } = useCreateQuestion();
  const { t } = useTranslation('client_questions');

  const onReply = async text => {
    const payload = {
      reply_to: replyMessageID,
      comment_text: text,
    };

    const res = await createNewQuestion(payload);
    if (res?.success) {
      setReplyMessageID(null);
      Notify.success({ text: t('card.message_created') });
    }
  };

  return (
    <DropdownBase
      render={({ open, setOpen }) => (
        <div className={classnames(s.wrapper, `level_${question.level}`)}>
          <div key={question.id} className={s.card}>
            <div
              className={classnames(
                s.card_avatar,
                question.status === QUESTION_STATUSES.draft &&
                  s.card_avatar_disabled,
              )}
            >
              <Avatar
                size={50}
                nameInitials={getNameInitials(
                  question.user.first_name,
                  question.user.last_name,
                )}
                image={question.user.photo}
                alt={question.user.full_name}
              />
            </div>
            <div className={s.card_content}>
              <div className={s.card_name}>
                <div>{question.user.full_name}</div>
                {question.status !== QUESTION_STATUSES.draft && (
                  <div className={s.card_flag}>
                    <img
                      src={getFlagURL(question.user.country_code)}
                      alt={question.user.country_code}
                    />
                  </div>
                )}
              </div>
              <div
                className={classnames(
                  s.card_question,
                  question.status === QUESTION_STATUSES.draft &&
                    s.card_question_disabled,
                )}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  allowedElements={[
                    'p', 'br', 'strong', 'em', 'del', 'code', 'pre',
                    'blockquote', 'ul', 'ol', 'li',
                    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                    'table', 'thead', 'tbody', 'tr', 'th', 'td',
                    'a', 'hr',
                  ]}
                  components={{
                    a: ({ href, children }) => (
                      <a href={href} target="_blank" rel="noopener noreferrer">
                        {children}
                      </a>
                    ),
                  }}
                >
                  {question.comment_text}
                </ReactMarkdown>
              </div>
              {question.status === QUESTION_STATUSES.draft ? (
                <div className={s.card_moderation}>
                  <div className={s.card_moderation_title}>
                    {t('card.moderation')}{' '}
                  </div>
                  {/* <button */}
                  {/*   type="button" */}
                  {/*   onClick={() => null} */}
                  {/*   className={s.card_delete_btn} */}
                  {/* > */}
                  {/*   Delete */}
                  {/* </button> */}
                </div>
              ) : (
                <div className={s.card_controls}>
                  <div className={s.card_time}>
                    {remainingTime(question.created_at)}
                  </div>

                  {!!question.children.length && question.level === 1 && (
                    <button
                      type="button"
                      onClick={() => setOpen(!open)}
                      className={s.card_view_btn}
                    >
                      {open ? t('card.close_replies') : t('card.view_replies')}
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => setReplyMessageID(question.id)}
                    className={s.card_view_btn}
                  >
                    {t('card.reply')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {replyMessageID &&
            (isVerified ? (
              <WriteMessage
                onCancel={() => setReplyMessageID(null)}
                onReply={onReply}
                className={s.card_message}
              />
            ) : (
              <div className={s.card_verify}>
                <div className={s.card_verify_text}>
                  {t('card.verify_yourself_text')}
                </div>
                <RoundLink
                  path={APP_LINKS.profile}
                  label={t('card.verify_yourself_button')}
                  className={s.card_verify_link}
                  fillBackground
                  fullWidth
                />
              </div>
            ))}

          <div
            className={classnames(
              s.card_drop,
              (open || question.level > 1) && 'open',
            )}
          >
            {question.children.map(item => (
              <QuestionCard key={item.id} question={item} />
            ))}
          </div>
        </div>
      )}
    />
  );
};
